import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import './gipform.css';
import Select from "react-select";


// ====================================================================
// FUNCTION TO PARSE EXCEL DATA
// ====================================================================
const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const allCells = Object.keys(worksheet)
          .filter((k) => k[0] !== '!')
          .reduce((acc, key) => {
            acc[key] = String(worksheet[key].v).trim();
            return acc;
          }, {});

        const findCellByKeyword = (keyword) =>
          Object.entries(allCells).find(([cell, val]) =>
            val.toLowerCase().includes(keyword.toLowerCase())
          );

        const getValueRight = (cellKey) => {
          if (!cellKey) return '';
          const match = cellKey.match(/([A-Z]+)(\d+)/);
          if (!match) return '';
          let [_, col, row] = match;
          const rowNum = parseInt(row);
          let nextColCode = col.charCodeAt(0) + 1;
          while (nextColCode <= 90) {
            const nextKey = `${String.fromCharCode(nextColCode)}${rowNum}`;
            if (worksheet[nextKey]?.v) return String(worksheet[nextKey].v).trim();
            nextColCode++;
          }
          return '';
        };

        const extractNumber = (val) => {
          if (!val) return 0;
          const num = val.toString().replace(/[^\d.-]/g, '');
          return num ? parseFloat(num) : 0;
        };

        const partNoCell = findCellByKeyword('part no');
        const lotQtyCell = findCellByKeyword('lot qty');
        const dateCell = findCellByKeyword('date');
        const partNameCell = findCellByKeyword('partname');
        const batchNoCell = findCellByKeyword('batch no');
        const inspQtyCell = findCellByKeyword('inspection qty');
        const rejectQtyCell = findCellByKeyword('rejection qty');

        const headData = {
          PartNo: getValueRight(partNoCell?.[0]) || '',
          PartName: getValueRight(partNameCell?.[0]) || '',
          BatchNo: getValueRight(batchNoCell?.[0]) || '',
          LotQty: extractNumber(getValueRight(lotQtyCell?.[0])),
          InspectionQty: extractNumber(getValueRight(inspQtyCell?.[0])),
          RejectionQty: extractNumber(getValueRight(rejectQtyCell?.[0])),
          GrnDate: (() => {
            const raw = getValueRight(dateCell?.[0]);
            if (!raw) return new Date().toISOString().split('T')[0];
            const parsed = new Date(raw);
            if (!isNaN(parsed)) return parsed.toISOString().split('T')[0];
            const parts = raw.split(/[-/]/);
            if (parts.length === 3) {
              const [d, m, y] = parts.map((p) => parseInt(p, 10));
              return new Date(y, m - 1, d).toISOString().split('T')[0];
            }
            return new Date().toISOString().split('T')[0];
          })(),
        };

        console.log("✅ Parsed Header Data:", headData);

        const customHeaders = [
          "SlNo", "ItemSpec", "Description", "LowerLimit", "UpperLimit",
          "Obs1", "Obs2", "Obs3", "Obs4", "Obs5", "Obs6", "Obs7", "Obs8", "Obs9", "Obs10",
          "Result", "Remark"
        ];

        const rawItemRows = XLSX.utils.sheet_to_json(worksheet, {
          header: customHeaders,
          range: 8,
          defval: ''
        });

        const itemData = rawItemRows
          .filter(row => {
            const spec = String(row.ItemSpec).trim();
            const desc = String(row.Description).trim();
            return (
              spec !== '' &&
              !spec.toLowerCase().includes('approved') &&
              !desc.toLowerCase().includes('approved')
            );
          })
          .map((row, i) => ({
            ItemID: i + 1,
            SlNo: row.SlNo || i + 1,
            ItemSpec: String(row.ItemSpec).trim(),
            Description: String(row.Description).trim(),
            LowerLimit: String(row.LowerLimit).trim(),
            UpperLimit: String(row.UpperLimit).trim(),
            Obs1: String(row.Obs1).trim(),
            Obs2: String(row.Obs2).trim(),
            Obs3: String(row.Obs3).trim(),
            Obs4: String(row.Obs4).trim(),
            Obs5: String(row.Obs5).trim(),
            Obs6: String(row.Obs6).trim(),
            Obs7: String(row.Obs7).trim(),
            Obs8: String(row.Obs8).trim(),
            Obs9: String(row.Obs9).trim(),
            Obs10: String(row.Obs10).trim(),
            Result: String(row.Result).trim(),
            Remark: String(row.Remark).trim(),
          }));

        resolve({ headData, itemData });
      } catch (error) {
        reject(new Error(`Error parsing Excel file: ${error.message}`));
      }
    };

    reader.onerror = (error) =>
      reject(new Error(`File read error: ${error.message}`));

    reader.readAsArrayBuffer(file);
  });
};

// ====================================================================
// GIP FORM COMPONENT
// ====================================================================

const GipForm = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        supplier: '',
        gipNo: '',
        gipDate: new Date().toISOString().split('T')[0],
        uploadedFile: null,
        userName: 'InspectionUser'
    });

    const [suppliers, setSuppliers] = useState([]);
    const [gipData, setGipData] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedGip, setSelectedGip] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogout = () => {
        sessionStorage.removeItem('userName');
        sessionStorage.removeItem('databaseKey');
        navigate('/login');
    };

    const handleBack = () => {
        navigate('/dashboard');
    };

    // Load suppliers on component mount
    useEffect(() => {
        const loadSuppliers = async () => {
            try {
                setLoading(true);
                const userName = sessionStorage.getItem('userName');
                const result = await api.getSupplierDatas(userName);
                const supplierList = result?.GetSupplierDatasApi?.Table || [];
                setSuppliers(supplierList);
            } catch (error) {
                console.error('Error loading suppliers:', error);
                setUploadStatus('❌ Failed to load suppliers');
            } finally {
                setLoading(false);
            }
        };
        loadSuppliers();
    }, []);

    // Load GIP data when supplier is selected
    useEffect(() => {
        if (selectedSupplier) {
            const loadGipData = async () => {
                try {
                    setLoading(true);
                    const result = await api.getGipDatas(selectedSupplier.Vid);
                    const gipList = result?.GetGipDatasApi?.Table || [];
                    setGipData(gipList);
                } catch (error) {
                    console.error('Error loading GIP data:', error);
                    setUploadStatus('❌ Failed to load GIP data');
                } finally {
                    setLoading(false);
                }
            };
            loadGipData();
        } else {
            setGipData([]);
        }
    }, [selectedSupplier]);

    const handleSupplierChange = (e) => {
        const vid = parseInt(e.target.value);
        const supplier = suppliers.find(s => s.Vid === vid);
        setSelectedSupplier(supplier);
        setSelectedGip(null);
        setFormData(prev => ({ ...prev, supplier: supplier ? supplier.Vname : '' }));
    };

    const handleGipChange = (e) => {
        const gipId = parseInt(e.target.value);
        const gip = gipData.find(g => g.GIPId === gipId);
        setSelectedGip(gip);
        setFormData(prev => ({
            ...prev,
            gipNo: gip ? gip.GIPNo : '',
            gipDate: gip ? gip.GIPDate.split('T')[0] : new Date().toISOString().split('T')[0]
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setFormData(prevData => ({ ...prevData, uploadedFile: file }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({ ...prevData, [name]: value }));
    };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadStatus('Uploading...');

    if (!formData.uploadedFile) {
      setUploadStatus('❌ Please select an Excel file.');
      return;
    }

    try {
      const { headData, itemData } = await parseExcelFile(formData.uploadedFile);

      if (itemData.length === 0) {
        setUploadStatus('❌ No valid item data found.');
        return;
      }

      // ✅ Construct Final JSON Payload (no Base64)
      const payload = {
        GrnNo: formData.gipNo,
        GrnId: selectedGip?.GIPId || formData.gipNo,
        UserName: formData.userName,
        GrnDate: formData.gipDate,
        PartNo: headData.PartNo,
        PartName: headData.PartName,
        LotQty: headData.LotQty,
        BatchNo: headData.BatchNo,
        InspectionQty: headData.InspectionQty,
        RejectionQty: headData.RejectionQty,
        SourceFile: formData.uploadedFile.name, // ✅ only filename
        Items: itemData,
        InspectionDate: new Date().toISOString(),
      };

      console.log("🚀 Payload Sent to API:", payload);

      const result = await api.uploadInspectionReport(payload);

      setUploadStatus(`✅ Success: ${result.message || 'Data uploaded successfully.'}`);
    } catch (err) {
      console.error(err);
      setUploadStatus(`❌ Error: ${err.message}`);
    }
  };

    return (
        <div className="gip-form-container">
            <div className="form-header">
                <button onClick={handleBack} className="back-button">← Back to Dashboard</button>
                <button onClick={handleLogout} className="logout-button">Logout</button>
            </div>
            <h2 className="gip-form-heading">GRN Inspection Report</h2>
            <form onSubmit={handleSubmit} className="gip-form">

               <div className="form-group">
  <label className="form-label">Supplier:</label>
  <Select
    className="form-input"
    placeholder="Select Supplier..."
    options={suppliers.map(s => ({
      value: s.Vid,
      label: `${s.Vname} (${s.Vcode})`
    }))}
    value={
      selectedSupplier
        ? { value: selectedSupplier.Vid, label: `${selectedSupplier.Vname} (${selectedSupplier.Vcode})` }
        : null
    }
    onChange={(option) => {
  if (!option) {
    setSelectedSupplier(null);
    setSelectedGip(null);
    setFormData(prev => ({ ...prev, supplier: '' }));
    return;
  }

  const supplier = suppliers.find(s => s.Vid === option.value);
  setSelectedSupplier(supplier);
  setSelectedGip(null);
  setFormData(prev => ({ ...prev, supplier: supplier?.Vname || '' }));
}}

    isLoading={loading}
    isClearable
  />
</div>

            <div className="form-group">
  <label className="form-label">GIP:</label>
  <Select
    className="form-input"
    placeholder={selectedSupplier ? "Select GIP..." : "Select supplier first"}
    options={gipData.map(g => ({
      value: g.GIPId,
      label: `${g.GIPNo} - ${g.DCNo}`
    }))}
    value={
      selectedGip
        ? { value: selectedGip.GIPId, label: `${selectedGip.GIPNo} - ${selectedGip.DCNo}` }
        : null
    }
   onChange={(option) => {
  if (!option) {
    setSelectedGip(null);
    setFormData(prev => ({ ...prev, gipNo: '', gipDate: '' }));
    return;
  }

  const gip = gipData.find(g => g.GIPId === option.value);
  setSelectedGip(gip);
  setFormData(prev => ({
    ...prev,
    gipNo: gip?.GIPNo || '',
    gipDate: gip?.GIPDate?.split('T')[0] || new Date().toISOString().split('T')[0]
  }));
}}
    isDisabled={!selectedSupplier}
    isClearable
    isSearchable
  />
</div>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="gipNo" className="form-label">GRN No:</label>
                        <input
                            type="text"
                            id="gipNo"
                            name="gipNo"
                            value={formData.gipNo}
                            onChange={handleInputChange}
                            required
                            className="form-input"
                            placeholder="Enter GRN Number"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="gipDate" className="form-label">GIP Date:</label>
                        <input
                            type="date"
                            id="gipDate"
                            name="gipDate"
                            value={formData.gipDate}
                            onChange={handleInputChange}
                            required
                            className="form-input"
                        />
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="uploadedFile" className="form-label">Upload Excel File:</label>
                    <input
                        type="file"
                        id="uploadedFile"
                        name="uploadedFile"
                        onChange={handleFileChange}
                        required
                        className="form-input form-file-input"
                        accept=".xlsx, .xls" 
                    />
                    {formData.uploadedFile && (
                        <p className="form-file-name">Selected File: 📄 **{formData.uploadedFile.name}**</p>
                    )}
                </div>

                <button 
                    type="submit" 
                    className="form-submit-button" 
                    disabled={uploadStatus.startsWith('Uploading...') || !formData.uploadedFile} 
                >
                    {uploadStatus.startsWith('Uploading...') ? 'Submitting...' : 'Submit Inspection Data'}
                </button>

                {uploadStatus && (
                    <p className={`upload-status ${uploadStatus.startsWith('✅') ? 'success' : uploadStatus.startsWith('❌') ? 'failure' : ''}`}>
                        {uploadStatus}
                    </p>
                )}
            </form>
        </div>
    );
};

export default GipForm;